import React, { useCallback, useEffect, useState } from "react";

/**
 * Botón flotante global: ir al inicio / al final de la página.
 * Útil en formularios largos (historia clínica, pacientes, parámetros, etc.).
 */
function ScrollToEndsButton() {
    const [visible, setVisible] = useState(false);
    const [atTop, setAtTop] = useState(true);
    const [atBottom, setAtBottom] = useState(false);

    const getCandidates = useCallback(() => {
        const list = [];
        const wrapper = document.querySelector(".content-wrapper");
        const root = document.scrollingElement || document.documentElement;
        if (wrapper) list.push(wrapper);
        if (root) list.push(root);
        if (document.body && document.body !== root) list.push(document.body);
        return list;
    }, []);

    const readScrollTop = (el) => {
        if (!el) return 0;
        if (el === document.documentElement || el === document.scrollingElement || el === document.body) {
            return window.pageYOffset || el.scrollTop || 0;
        }
        return el.scrollTop || 0;
    };

    const getPrimaryScroller = useCallback(() => {
        let best = document.scrollingElement || document.documentElement;
        let bestOverflow = 0;

        getCandidates().forEach((el) => {
            const clientHeight = (el === document.documentElement || el === document.scrollingElement || el === document.body)
                ? window.innerHeight
                : el.clientHeight;
            const overflow = el.scrollHeight - clientHeight;
            if (overflow > bestOverflow) {
                bestOverflow = overflow;
                best = el;
            }
        });

        return best;
    }, [getCandidates]);

    const updateVisibility = useCallback(() => {
        const el = getPrimaryScroller();
        const scrollTop = readScrollTop(el);
        const isDoc = el === document.documentElement || el === document.scrollingElement || el === document.body;
        const clientHeight = isDoc ? window.innerHeight : el.clientHeight;
        const scrollHeight = el.scrollHeight;
        const canScroll = scrollHeight > clientHeight + 80;

        // También considerar si window o content-wrapper tienen offset residual
        const windowTop = window.pageYOffset || 0;
        const wrapper = document.querySelector(".content-wrapper");
        const wrapperTop = wrapper ? wrapper.scrollTop : 0;
        const maxTop = Math.max(scrollTop, windowTop, wrapperTop);

        setVisible(canScroll || maxTop > 2);
        setAtTop(maxTop <= 2);
        setAtBottom(scrollTop + clientHeight >= scrollHeight - 8);
    }, [getPrimaryScroller]);

    useEffect(() => {
        updateVisibility();
        const onScroll = () => updateVisibility();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);

        const wrapper = document.querySelector(".content-wrapper");
        if (wrapper) {
            wrapper.addEventListener("scroll", onScroll, { passive: true });
        }

        const interval = window.setInterval(updateVisibility, 800);

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (wrapper) {
                wrapper.removeEventListener("scroll", onScroll);
            }
            window.clearInterval(interval);
        };
    }, [updateVisibility]);

    const scrollAllToTop = () => {
        const optsSmooth = { top: 0, left: 0, behavior: "smooth" };
        const optsInstant = { top: 0, left: 0, behavior: "auto" };

        window.scrollTo(optsSmooth);
        getCandidates().forEach((el) => {
            try {
                if (typeof el.scrollTo === "function") {
                    el.scrollTo(optsSmooth);
                } else {
                    el.scrollTop = 0;
                }
            } catch (e) {
                el.scrollTop = 0;
            }
        });

        // Smooth a veces se queda corto (AdminLTE + header fijo): forzar tope real
        window.setTimeout(() => {
            window.scrollTo(optsInstant);
            getCandidates().forEach((el) => {
                el.scrollTop = 0;
            });
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
            updateVisibility();
        }, 420);
    };

    const scrollToBottom = () => {
        const el = getPrimaryScroller();
        const isDoc = el === document.documentElement || el === document.scrollingElement || el === document.body;
        const top = el.scrollHeight;

        if (isDoc) {
            window.scrollTo({ top, behavior: "smooth" });
        } else {
            el.scrollTo({ top, behavior: "smooth" });
            // por si también hay scroll en window
            window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
        }

        window.setTimeout(() => {
            if (isDoc) {
                window.scrollTo({ top: el.scrollHeight, behavior: "auto" });
            } else {
                el.scrollTop = el.scrollHeight;
            }
            updateVisibility();
        }, 420);
    };

    if (!visible) {
        return null;
    }

    return (
        <div className="smile-scroll-ends" role="group" aria-label="Navegación de scroll">
            <button
                type="button"
                className="smile-scroll-ends__btn"
                title="Ir al principio"
                aria-label="Ir al principio de la página"
                disabled={atTop}
                onClick={scrollAllToTop}
            >
                <i className="fas fa-chevron-up" aria-hidden="true" />
            </button>
            <button
                type="button"
                className="smile-scroll-ends__btn"
                title="Ir al final"
                aria-label="Ir al final de la página"
                disabled={atBottom}
                onClick={scrollToBottom}
            >
                <i className="fas fa-chevron-down" aria-hidden="true" />
            </button>
            <style>{`
                .smile-scroll-ends {
                    position: fixed;
                    right: 1.1rem;
                    bottom: 4.5rem;
                    z-index: 1040;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .smile-scroll-ends__btn {
                    width: 42px;
                    height: 42px;
                    border-radius: 999px;
                    border: 1px solid rgba(23, 162, 184, 0.35);
                    background: #17a2b8;
                    color: #fff;
                    box-shadow: 0 6px 18px rgba(23, 162, 184, 0.28);
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform .15s ease, background .15s ease, opacity .15s ease;
                }
                .smile-scroll-ends__btn:hover:not(:disabled) {
                    background: #138496;
                    transform: translateY(-1px);
                }
                .smile-scroll-ends__btn:disabled {
                    opacity: 0.35;
                    cursor: default;
                    box-shadow: none;
                }
                @media (max-width: 576px) {
                    .smile-scroll-ends {
                        right: 0.75rem;
                        bottom: 4rem;
                    }
                    .smile-scroll-ends__btn {
                        width: 38px;
                        height: 38px;
                    }
                }
            `}</style>
        </div>
    );
}

export default ScrollToEndsButton;
